import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { formatCount } from '../mock';
import { Crown, Users, Film, MessageSquare, Eye, Settings, Trash2, Shield, UserCog, Loader2, Tag, Plus, CheckCircle2, XCircle, Megaphone, Key, FileText, Wallet, Code2, Save, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { ownerStats, ownerUpdateSettings, ownerListUsers, ownerSetRole, ownerDeleteUser, listCategories, createCategory, deleteCategory } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import RoleBadge from '../components/RoleBadge';
import OwnerApiKeys from '../components/OwnerApiKeys';
import OwnerLegalEditor from '../components/OwnerLegalEditor';
import OwnerPayouts from '../components/OwnerPayouts';
import OwnerPricing from '../components/OwnerPricing';
import { Link } from 'react-router-dom';

export default function Owner() {
  const { user, refreshCategories, refreshSettings } = useApp();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, c] = await Promise.all([ownerStats(), ownerListUsers(), listCategories()]);
      setStats(s); setUsers(u); setCategories(c);
    } catch (err) {
      // handled below
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === 'owner') load(); else setLoading(false); }, [user]);

  if (user?.role !== 'owner') {
    return (
      <div className="py-10">
        <div className="card-shell p-6 max-w-[560px]">
          <div className="flex items-center gap-2 text-[#ffcf52]"><Crown className="w-5 h-5" /> <span className="font-semibold">Owner only</span></div>
          <p className="text-white/65 mt-2 text-[14px]">This dashboard is reserved for the platform owner.</p>
        </div>
      </div>
    );
  }
  if (loading || !stats) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  const toggleSetting = async (key, value) => {
    try {
      const s = await ownerUpdateSettings({ [key]: value });
      setStats(prev => ({ ...prev, settings: s }));
      await refreshSettings();
      toast.success('Settings updated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Update failed'); }
  };

  const doSetRole = async (uid, role) => {
    try { const u = await ownerSetRole(uid, role); setUsers(list => list.map(x => x.id === uid ? u : x)); toast.success(`Role set to ${role}`); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const doDeleteUser = async (uid) => {
    if (!window.confirm('Permanently delete this user, their channel and videos?')) return;
    try { await ownerDeleteUser(uid); setUsers(list => list.filter(x => x.id !== uid)); toast.success('User deleted'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try { const c = await createCategory(newCat.trim()); setCategories(list => [...list, c]); setNewCat(''); await refreshCategories(); toast.success('Category added'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const removeCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await deleteCategory(id); setCategories(list => list.filter(c => c.id !== id)); await refreshCategories(); toast.success('Removed'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const StatCard = ({ ic: Ic, color, label, value }) => (
    <div className="card-shell p-5">
      <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: color + '22', border: '1px solid ' + color + '55' }}>
        <Ic className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-display text-[30px] mt-3 leading-none">{value}</div>
      <div className="text-[12px] text-white/55 mt-1">{label}</div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2">
        <RoleBadge role="owner" />
        <span className="text-white/45 text-[12px]">Highest tier — full platform control</span>
      </div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-3 leading-none">Platform ownership</h1>
      <p className="text-white/60 text-[14px] mt-1">Settings, users and categories. You also have all admin moderation powers.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
        <StatCard ic={Users}    color="#22d3ee" label="Users"    value={stats.users} />
        <StatCard ic={UserCog}  color="#a78bfa" label="Channels" value={stats.channels} />
        <StatCard ic={Film}     color="#ff2e63" label="Videos"   value={stats.videos} />
        <StatCard ic={MessageSquare} color="#f59e0b" label="Comments" value={stats.comments} />
        <StatCard ic={Eye}      color="#34d399" label="Views"    value={formatCount(stats.views)} />
      </div>

      <div className="mt-8">
        <Link to="/owner/ads" className="card-shell p-5 flex items-center justify-between hover-lift block" style={{ borderColor: 'rgba(255,207,82,0.3)', background: 'linear-gradient(90deg, rgba(255,207,82,0.08), rgba(255,207,82,0.02))' }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: 'rgba(255,207,82,0.15)', border: '1px solid rgba(255,207,82,0.35)' }}>
              <Megaphone className="w-5 h-5 text-[#ffcf52]" />
            </div>
            <div>
              <div className="font-semibold">Ads & Ad Server</div>
              <div className="text-[13px] text-white/60">Create platform banners, pre-rolls, promote creators and paste external ad server snippets.</div>
            </div>
          </div>
          <div className="text-[13px] text-[#ffcf52] flex items-center gap-1">Open →</div>
        </Link>
      </div>

      <Tabs defaultValue="settings" className="mt-8">
        <TabsList className="bg-white/[0.04] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" /> Settings</TabsTrigger>
          <TabsTrigger value="categories"><Tag className="w-4 h-4 mr-2" /> Categories</TabsTrigger>
          <TabsTrigger value="users"><Shield className="w-4 h-4 mr-2" /> Users</TabsTrigger>
          <TabsTrigger value="apikeys"><Key className="w-4 h-4 mr-2" /> API</TabsTrigger>
          <TabsTrigger value="pricing"><CreditCard className="w-4 h-4 mr-2" /> Pricing</TabsTrigger>
          <TabsTrigger value="payouts"><Wallet className="w-4 h-4 mr-2" /> Payouts</TabsTrigger>
          <TabsTrigger value="legal"><FileText className="w-4 h-4 mr-2" /> Legal</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-6">
          <div className="card-shell p-6 max-w-[920px]">
            <h3 className="row-title text-white">Platform toggles</h3>
            <div className="mt-5 space-y-4">
              <SettingRow
                title="Allow users to create custom categories"
                desc="When disabled, only admins and owners can add categories."
                value={!!stats.settings?.allow_custom_categories}
                onChange={v => toggleSetting('allow_custom_categories', v)}
              />
              <SettingRow
                title="Allow new sign-ups"
                desc="When disabled, the public signup flow is blocked. Existing users are unaffected."
                value={!!stats.settings?.allow_signups}
                onChange={v => toggleSetting('allow_signups', v)}
              />
              <SettingRow
                title="Allow video downloads (platform-wide)"
                desc="Global kill-switch. When off, the Download button is disabled for every viewer, regardless of per-video or channel settings."
                value={stats.settings?.allow_downloads !== false}
                onChange={v => toggleSetting('allow_downloads', v)}
              />
              <SettingRow
                title="Accept crypto payments"
                desc="When on, Stripe Checkout offers USDC alongside card payments (requires Stripe crypto enabled on your account)."
                value={!!stats.settings?.crypto_enabled}
                onChange={v => toggleSetting('crypto_enabled', v)}
              />
              <SettingRow
                title="Auto-send invoices"
                desc="Email a Stripe invoice/receipt to buyers on every successful purchase."
                value={stats.settings?.invoicing_enabled !== false}
                onChange={v => toggleSetting('invoicing_enabled', v)}
              />
              <SettingRow
                title="Collect tax automatically"
                desc="Stripe Tax handles US sales tax and EU VAT. Enable Tax in your Stripe Dashboard first."
                value={!!stats.settings?.tax_enabled}
                onChange={v => toggleSetting('tax_enabled', v)}
              />
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <div className="flex items-center gap-2"><Save className="w-4 h-4 text-[#a78bfa]" /><h3 className="row-title text-white m-0">Role-based storage defaults</h3></div>
              <p className="text-white/60 text-[13px] mt-1">Quotas (in MB) applied to users without a per-user override.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <StorageSettingRow
                  label="Free plan storage (MB)"
                  initial={stats.settings?.free_storage_mb ?? 1024}
                  onSave={v => toggleSetting('free_storage_mb', v)}
                  testid="free-storage-mb-input"
                />
                <StorageSettingRow
                  label="Admin storage (MB)"
                  initial={stats.settings?.admin_storage_mb ?? 5120000}
                  onSave={v => toggleSetting('admin_storage_mb', v)}
                  testid="admin-storage-mb-input"
                />
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <div className="flex items-center gap-2"><Code2 className="w-4 h-4 text-[#ffcf52]" /><h3 className="row-title text-white m-0">AdSense / third-party head snippet</h3></div>
              <p className="text-white/60 text-[13px] mt-1">Paste AdSense auto-ads code, Google Analytics, or any &lt;script&gt;/&lt;meta&gt; tags. Injected into every page &lt;head&gt;.</p>
              <AdSenseEditor initial={stats.settings?.adsense_head || ''} onSave={val => toggleSetting('adsense_head', val)} />
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <h3 className="row-title text-white">Creator revenue share</h3>
              <p className="text-white/60 text-[13px] mt-1">Fraction of ad revenue paid to creators. Applied to every ad event on monetized videos.</p>
              <RevShareEditor initial={stats.settings?.creator_rev_share ?? 0.55} onSave={val => toggleSetting('creator_rev_share', val)} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="card-shell p-6 max-w-[900px]">
            <h3 className="row-title text-white">Categories</h3>
            <p className="text-white/60 text-[13px] mt-1">Default categories include Trending, Shorts, Movies, TV Shows and more. Custom categories can be toggled for users under Settings.</p>
            <form onSubmit={addCategory} className="mt-5 flex gap-2">
              <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category name (e.g. Podcasts)" className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#ff2e63]/60 text-[14px]" />
              <button className="btn-primary"><Plus className="w-4 h-4" /> Add</button>
            </form>
            <div className="mt-5 flex flex-wrap gap-2">
              {categories.map(c => (
                <span key={c.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[13px]" style={{ background: c.type === 'default' ? 'rgba(255,255,255,0.04)' : 'rgba(255,46,99,0.1)', borderColor: c.type === 'default' ? 'rgba(255,255,255,0.12)' : 'rgba(255,46,99,0.35)' }}>
                  <span className="font-semibold">{c.name}</span>
                  <span className="text-white/45 text-[11px] uppercase tracking-wider">{c.type}</span>
                  <button onClick={() => removeCategory(c.id)} className="text-white/55 hover:text-[#ff6b8a]" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              ))}
              {categories.length === 0 && <div className="text-white/55 text-[13px]">No categories yet.</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="card-shell overflow-hidden">
            <div className="p-5"><h3 className="row-title text-white">Users ({users.length})</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]">
                <thead className="bg-white/[0.03] text-white/55">
                  <tr>
                    <th className="text-left px-5 py-2 font-semibold">User</th>
                    <th className="text-left px-3 py-2 font-semibold">Email</th>
                    <th className="text-left px-3 py-2 font-semibold">Plan</th>
                    <th className="text-left px-3 py-2 font-semibold">Role</th>
                    <th className="px-5 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <img src={u.avatar || `https://i.pravatar.cc/60?u=${u.email}`} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                          <div className="min-w-0">
                            <div className="font-semibold flex items-center gap-2">{u.name} <RoleBadge role={u.role} size="sm" /></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-white/80">{u.email}</td>
                      <td className="px-3 py-3 capitalize">{u.plan}</td>
                      <td className="px-3 py-3">
                        <Select value={u.role} onValueChange={(v) => doSetRole(u.id, v)} disabled={u.id === user.id}>
                          <SelectTrigger className="bg-white/[0.04] border-white/10 h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#141418] border-white/10 text-white">
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="creator">Creator</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => doDeleteUser(u.id)} disabled={u.id === user.id} className="text-white/60 hover:text-[#ff6b8a] disabled:opacity-30" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="apikeys" className="mt-6">
          <OwnerApiKeys />
        </TabsContent>

        <TabsContent value="pricing" className="mt-6">
          <OwnerPricing settings={stats.settings} onSettingsChange={(s) => setStats(prev => ({ ...prev, settings: s }))} />
        </TabsContent>

        <TabsContent value="payouts" className="mt-6">
          <OwnerPayouts />
        </TabsContent>

        <TabsContent value="legal" className="mt-6">
          <OwnerLegalEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdSenseEditor({ initial, onSave }) {
  const [val, setVal] = useState(initial || '');
  return (
    <div className="mt-4">
      <textarea value={val} onChange={e => setVal(e.target.value)} rows={6}
        placeholder={'<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-..." crossorigin="anonymous"></script>'}
        className="input-field w-full font-mono text-[12.5px]" data-testid="adsense-head-input" />
      <div className="mt-2 flex justify-end">
        <button onClick={() => onSave(val)} className="btn-primary" data-testid="adsense-save-btn">
          <Save className="w-4 h-4" /> Save head snippet
        </button>
      </div>
    </div>
  );
}

function RevShareEditor({ initial, onSave }) {
  const [val, setVal] = useState(String(Math.round((initial ?? 0.55) * 100)));
  return (
    <div className="mt-4 flex items-center gap-3">
      <input type="number" min="0" max="100" value={val} onChange={e => setVal(e.target.value)} className="input-field w-28" data-testid="revshare-input" />
      <span className="text-white/65 text-[13.5px]">% to creator (rest to platform)</span>
      <button onClick={() => onSave(Math.max(0, Math.min(1, parseFloat(val) / 100)))} className="btn-primary" data-testid="revshare-save-btn">
        <Save className="w-4 h-4" /> Save
      </button>
    </div>
  );
}

function StorageSettingRow({ label, initial, onSave, testid }) {
  const [v, setV] = useState(initial);
  React.useEffect(() => { setV(initial); }, [initial]);
  return (
    <div className="flex items-end gap-2">
      <label className="block flex-1">
        <span className="text-[12px] text-white/60">{label}</span>
        <input type="number" min="0" value={v} onChange={e => setV(parseInt(e.target.value) || 0)} className="input-field w-full mt-1" data-testid={testid} />
      </label>
      <button onClick={() => onSave(v)} className="btn-primary" aria-label="Save"><Save className="w-4 h-4" /></button>
    </div>
  );
}

function SettingRow({ title, desc, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5 last:border-0">
      <div>
        <div className="font-semibold text-[14.5px] flex items-center gap-2">{title} {value ? <CheckCircle2 className="w-4 h-4 text-[#22d3ee]" /> : <XCircle className="w-4 h-4 text-white/40" />}</div>
        <div className="text-white/60 text-[13px] mt-1">{desc}</div>
      </div>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}
